/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging */

import type { LocalTrack } from "twilio-video";

import {
  connect,
  createLocalTracks,
  createLocalVideoTrack,
  LocalAudioTrack,
  LocalVideoTrack,
  Room,
} from "twilio-video";

import type { LiveConsultConstructorParameters } from "@/App/components/liveConsult/types/LiveConsultConstructorParameters";
import type { LiveConsultService } from "@/App/components/liveConsult/types/LiveConsultService";
import type { PickUpLiveConsultParameters } from "@/App/components/liveConsult/types/PickUpLiveConsultParameters";

import { LiveConsultEvents } from "@/App/components/liveConsult/types/LiveConsultEvents";

export interface LiveConsult {
  audioIsDisabled: boolean;
  caseId: string;
  liveConsultService: LiveConsultService;
  localVideoTrack: LocalVideoTrack;
  room: Room;
  tracks: LocalTrack[] | LocalAudioTrack[] | LocalVideoTrack[];
  videoIsDisabled: boolean;
}

export class LiveConsult implements LiveConsult {
  constructor({ caseId, liveConsultService }: LiveConsultConstructorParameters) {
    this.audioIsDisabled = false;
    this.caseId = caseId;
    this.liveConsultService = liveConsultService;
    this.videoIsDisabled = false;
  }

  hangUp(): void {
    this.room.disconnect();

    this.localVideoTrack.stop();
    this.localVideoTrack.detach();

    this.tracks.forEach((track: LocalTrack) => {
      if (track instanceof LocalAudioTrack || track instanceof LocalVideoTrack) {
        track.stop();
      }
    });
  }

  async pickUp({ onParticipantConnected, onParticipantDisconnected }: PickUpLiveConsultParameters): Promise<void> {
    const { authToken, roomId } = await this.liveConsultService.getLiveConsultToken({
      caseId: this.caseId,
    });

    this.tracks = await createLocalTracks();

    this.room = await connect(authToken, { name: roomId, tracks: this.tracks });

    this.room.participants.forEach(onParticipantConnected);

    this.room.on(LiveConsultEvents.participantConnected, onParticipantConnected);

    this.room.on(LiveConsultEvents.participantDisconnected, onParticipantDisconnected);

    this.room.once(LiveConsultEvents.disconnected, () => this.room.participants.forEach(onParticipantDisconnected));

    this.localVideoTrack = await createLocalVideoTrack();
  }

  toggleAudio(): void {
    const method = this.audioIsDisabled ? "enable" : "disable";

    this.room.localParticipant.audioTracks.forEach((publication) => {
      publication.track[method]();
    });

    this.audioIsDisabled = !this.audioIsDisabled;
  }

  async toggleVideo(): Promise<void> {
    const method = this.videoIsDisabled ? "restart" : "stop";

    this.room.localParticipant.videoTracks.forEach((publication) => {
      publication.track[method]();
    });

    if (this.videoIsDisabled) {
      await this.localVideoTrack.restart();
    } else {
      await this.localVideoTrack.stop();
    }

    this.videoIsDisabled = !this.videoIsDisabled;
  }
}

export type { RemoteParticipant, RemoteTrackPublication, RemoteVideoTrack } from "twilio-video";

export { VideoTrack } from "twilio-video";
